"use client";

import { useRouter } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/useRouter";
import { SHOW_MODAL_QUERY_PARAM } from "@faire/deprecated-retailer-visitor-shared/components/App/AppModals/consts";
import { trackAppModalFrameworkShowAppModalActionCategoryImpression as trackAppModalOpen } from "@faire/web-api--source/events/appModalFramework/actionCategoryImpression/showAppModal";
import { useEnv } from "@faire/web/common/env/client";
import { getCurrentPath } from "@faire/web/common/Path";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import { AppModalDefinition } from "./AppModalDefinition";

interface AppModalsProps<E = unknown> {
  appModals: AppModalDefinition<E>[];
  appModalEnv: E;
}

export const useCanGoBack = () => {
  const history = useHistory();
  return () => {
    return history.length > 1;
  };
};

export const AppModals = <E = unknown,>({
  appModals,
  appModalEnv,
}: AppModalsProps<E>) => {
  const router = useRouter();

  const pathname = usePathname();
  const query = useSearchParams();

  // Hook to determine if we can use router.back() and stay within the app
  const canGoBack = useCanGoBack();

  const openModal = appModals.find((appModal) => {
    if (appModal.doNotShowOnRoutes?.match(pathname)) {
      return false;
    }

    if (
      appModal.showModalQueryParameterValueTrigger &&
      query.get(SHOW_MODAL_QUERY_PARAM) ===
        appModal.showModalQueryParameterValueTrigger
    ) {
      return true;
    }

    if (
      appModal.customQueryParameterTrigger &&
      query.get(appModal.customQueryParameterTrigger)
    ) {
      return true;
    }
    return false;
  });

  const onClose = () => {
    const newPath = getCurrentPath();

    newPath.delete(SHOW_MODAL_QUERY_PARAM);

    appModals.forEach((appModal) => {
      if (appModal.customQueryParameterTrigger) {
        newPath.delete(appModal.customQueryParameterTrigger);
      }

      appModal.onClose?.();
    });

    const newLocation = newPath.toString();

    if (openModal?.pushHistoryOnClose) {
      router.push(newLocation);
    } else {
      if (canGoBack()) {
        router.back();
      } else {
        router.replace(newLocation);
      }
    }
  };

  const NEXTJS_APPLICATION = useEnv()?.appName ?? "";

  useEffect(() => {
    if (openModal) {
      trackAppModalOpen(openModal.name, NEXTJS_APPLICATION);
    }
  }, [openModal]);

  if (!openModal) {
    return null;
  }

  return <openModal.AppModal onClose={onClose} appModalEnv={appModalEnv} />;
};
